import { useState, useCallback } from 'react';

/**
 * Custom hook for managing full screen state in split view components
 * @param {Object} options - Configuration options
 * @param {boolean} options.defaultFullScreen - Initial full screen state
 * @param {Function} options.onToggle - Callback when full screen is toggled
 * @returns {Object} Full screen state and controls
 */
const useFullScreen = (options = {}) => {
  const { defaultFullScreen = false, onToggle } = options;
  
  const [isFullScreen, setIsFullScreen] = useState(defaultFullScreen);
  const [previousState, setPreviousState] = useState(null);

  // Toggle full screen mode
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => {
      const newState = !prev;
      
      // Store previous state for restoration
      if (newState) {
        setPreviousState({ isFullScreen: prev });
      }
      
      // Call onToggle callback if provided
      if (onToggle) {
        onToggle(newState);
      }
      
      return newState;
    });
  }, [onToggle]);

  // Enter full screen mode
  const enterFullScreen = useCallback(() => {
    if (!isFullScreen) {
      setPreviousState({ isFullScreen });
      setIsFullScreen(true);
      
      if (onToggle) {
        onToggle(true);
      }
    }
  }, [isFullScreen, onToggle]);

  // Exit full screen mode
  const exitFullScreen = useCallback(() => {
    if (isFullScreen) {
      setIsFullScreen(false);
      setPreviousState(null);
      
      if (onToggle) {
        onToggle(false);
      }
    }
  }, [isFullScreen, onToggle]);

  // Get flex styles for split view panels
  const getLeftPanelStyles = useCallback((sidebarCollapsed = false) => {
    return {
      flex: isFullScreen ? 0 : (sidebarCollapsed ? 1 : 0.5),
      overflow: 'auto',
      transition: 'flex 0.3s ease',
      height: '100%',
      display: isFullScreen ? 'none' : 'block',
    };
  }, [isFullScreen]);

  const getRightPanelStyles = useCallback((sidebarCollapsed = false) => {
    return {
      flex: isFullScreen ? 1 : (sidebarCollapsed ? 0 : 0.5),
      overflow: 'auto',
      display: isFullScreen ? 'block' : (sidebarCollapsed ? 'none' : 'block'),
      transition: 'flex 0.3s ease',
      height: '100%',
    };
  }, [isFullScreen]);

  // Get content styles for full screen mode
  const getContentStyles = useCallback((customStyles = {}) => {
    return {
      ...customStyles,
      padding: isFullScreen ? '8px' : customStyles.padding || '16px',
      transition: 'padding 0.3s ease',
    };
  }, [isFullScreen]);

  return {
    // State
    isFullScreen,
    previousState,
    
    // Actions
    toggleFullScreen,
    enterFullScreen,
    exitFullScreen,
    
    // Style helpers
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles,
  };
};

export default useFullScreen;